import { resolveMediaUrl } from "../../../utils/media-url";
import { ORDER_INVALID_EVENT } from "../validation-feedback";
import React, { ReactNode, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "Redux/hooks";
import { EDITOR_SAVED_EVENT } from "../../tool-new/editor-feedback";
import ManageOrder from "../save-preview-delete";
import "./order-mobile.scss";

type ItemKind = "rebar" | "net" | "pile";

type MobileOrderProps = {
  detailsContent: ReactNode;
  openItemModal: (type: number) => void;
  onEditItem: (item: any) => void;
  onCopyItem: (item: any) => void;
  onDeleteItem: (item: any) => void;
  onMoveItem: (kind: ItemKind, item: any, direction: -1 | 1) => void;
  [key: string]: any;
};

const itemTypeNumbers: Record<ItemKind, number> = {
  rebar: 1,
  net: 2,
  pile: 3,
};

function getDiameter(item: any, kind: ItemKind) {
  if (kind === "rebar") {
    return item?.tool?.diameter?.steel_diameter;
  }
  if (kind === "net") {
    const x = item?.tool?.x_diameter?.steel_diameter;
    const y = item?.tool?.y_diameter?.steel_diameter;
    return x && y ? (x === y ? x : `${x} / ${y}`) : x || y;
  }
  return (
    item?.tool?.diameter?.steel_diameter ||
    item?.tool?.longitudinal_diameter?.steel_diameter ||
    item?.tool?.spiral_diameter?.steel_diameter
  );
}

function getLength(item: any, kind: ItemKind) {
  if (kind === "rebar") {
    const sides = item?.tool?.rebar_side;
    return Array.isArray(sides)
      ? sides.reduce((sum: number, side: any) => sum + Number(side?.length || 0), 0)
      : undefined;
  }
  if (kind === "net") {
    const x = item?.tool?.x_length;
    const y = item?.tool?.y_length;
    return x && y ? `${x} × ${y}` : x || y;
  }
  return item?.tool?.length || item?.tool?.total_length;
}

function OrderItemCard({
  item,
  index,
  total,
  kind,
  onEdit,
  onCopy,
  onDelete,
  onMove,
}: {
  item: any;
  index: number;
  total: number;
  kind: ItemKind;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
  onMove: (direction: -1 | 1) => void;
}) {
  const { t } = useTranslation(["common", "newOrder", "header"]);
  const [expanded, setExpanded] = useState(false);
  const quantity = Number(item?.quantity || 0);
  const unitWeight = Number(item?.tool?.unit_weight || 0);
  const diameter = getDiameter(item, kind);
  const length = getLength(item, kind);
  const preview = resolveMediaUrl(item?.tool?.preview_image?.file_field);
  const labelKey = kind === "rebar" ? "Rebar" : kind === "net" ? "Net" : "Cage";

  return (
    <article className="mobile-order-item" data-order-item-id={item.id}>
      <div className="mobile-order-item__summary">
        <button
          type="button"
          className="mobile-order-item__expand"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          <span className="mobile-order-item__number">{item?.row_number || index + 1}</span>
          <span className="mobile-order-item__identity">
            <strong>{item?.tool?.name || t(labelKey, { ns: "common" })}</strong>
            <small>
              {t("quantity", { ns: "common" })}: {quantity}
              {diameter ? ` · Ø${diameter}` : ""}
            </small>
          </span>
          <span className={"mobile-order-item__chevron" + (expanded ? " is-open" : "")} aria-hidden="true">⌄</span>
        </button>
      </div>

      {expanded && (
        <div className="mobile-order-item__details">
          {preview && <img src={preview} alt="" className="mobile-order-item__preview" />}
          <dl>
            {diameter && (
              <div><dt>{t("diameter", { ns: "common" })}</dt><dd>{diameter} mm</dd></div>
            )}
            {length && (
              <div><dt>{t("length", { ns: "common" })}</dt><dd>{length} cm</dd></div>
            )}
            <div><dt>{t("quantity", { ns: "common" })}</dt><dd>{quantity}</dd></div>
            <div><dt>{t("weight", { ns: "common" })}</dt><dd>{(unitWeight * quantity).toFixed(1)} kg</dd></div>
          </dl>
          {item?.production_notes && (
            <p className="mobile-order-item__note">
              <strong>{t("note", { ns: "common" })}:</strong> {item.production_notes}
            </p>
          )}
        </div>
      )}

      <div className="mobile-order-item__actions">
        <button type="button" onClick={onEdit}>{t("edit", { ns: "common" })}</button>
        <button type="button" onClick={onCopy}>{t("Duplicate", { ns: "newOrder" })}</button>
        <button type="button" onClick={() => onMove(-1)} disabled={index === 0} aria-label={t("move up", { ns: "newOrder" })}>↑</button>
        <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} aria-label={t("move down", { ns: "newOrder" })}>↓</button>
        <button type="button" className="is-danger" onClick={onDelete}>{t("Delete", { ns: "common" })}</button>
      </div>
    </article>
  );
}

function ItemSection({
  kind,
  items,
  title,
  addLabel,
  openItemModal,
  ...actions
}: {
  kind: ItemKind;
  items: any[];
  title: string;
  addLabel: string;
  openItemModal: (type: number) => void;
  onEditItem: (item: any) => void;
  onCopyItem: (item: any) => void;
  onDeleteItem: (item: any) => void;
  onMoveItem: (kind: ItemKind, item: any, direction: -1 | 1) => void;
}) {
  const { t } = useTranslation(["newOrder"]);

  return (
    <section className="mobile-order-section">
      <div className="mobile-order-section__heading">
        <div><h2>{title}</h2><span>{items.length}</span></div>
        <button type="button" onClick={() => openItemModal(itemTypeNumbers[kind])}>+ {addLabel}</button>
      </div>
      {items.length ? (
        <div className="mobile-order-items">
          {items.map((item, index) => (
            <OrderItemCard
              key={item?.id || `${kind}-${index}`}
              item={item}
              index={index}
              total={items.length}
              kind={kind}
              onEdit={() => actions.onEditItem(item)}
              onCopy={() => actions.onCopyItem(item)}
              onDelete={() => actions.onDeleteItem(item)}
              onMove={(direction) => actions.onMoveItem(kind, item, direction)}
            />
          ))}
        </div>
      ) : (
        <div className="mobile-order-empty">
          <p>{t("no items yet", { ns: "newOrder" })}</p>
          <button type="button" onClick={() => openItemModal(itemTypeNumbers[kind])}>+ {addLabel}</button>
        </div>
      )}
    </section>
  );
}

function OrderMobile(props: MobileOrderProps) {
  const { t } = useTranslation(["common", "newOrder", "header"]);
  const rightToLeft = useAppSelector((state) => state.root.rightToLeft);
  const rebars = useAppSelector((state) => state.rebar.currentOrderRebars);
  const nets = useAppSelector((state) => state.net.currentOrderNets);
  const piles = useAppSelector((state) => state.pile.currentOrderPiles);
  const [activeStep, setActiveStep] = useState("details");
  useEffect(() => {
    const onSaved = (event: Event) => {
      const { kind } = (event as CustomEvent).detail;
      setActiveStep(kind === "rebar" ? "rebars" : kind === "net" ? "nets" : "piles");
    };
    window.addEventListener(EDITOR_SAVED_EVENT, onSaved);
    return () => window.removeEventListener(EDITOR_SAVED_EVENT, onSaved);
  }, []);

  useEffect(() => {
    let frame = 0;
    const invalid = (event: Event) => {
      const field = (event as CustomEvent).detail.field;
      if (field === "add one tool") { setActiveStep("rebars"); return; }
      setActiveStep("details");
      frame = requestAnimationFrame(() => {
        const target = document.querySelector<HTMLElement>(`[data-order-field="${field}"]`) || document.querySelector<HTMLElement>(".mobile-order-details input");
        target?.focus({ preventScroll: true });
        target?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    };
    window.addEventListener(ORDER_INVALID_EVENT, invalid);
    return () => { window.removeEventListener(ORDER_INVALID_EVENT, invalid); cancelAnimationFrame(frame); };
  }, []);

  const steps = useMemo(() => [
    { id: "details", label: t("Order Details", { ns: "newOrder" }) },
    { id: "rebars", label: t("Rebars", { ns: "newOrder" }), count: rebars.length },
    { id: "nets", label: t("Nets", { ns: "newOrder" }), count: nets.length },
    { id: "piles", label: t("Cages", { ns: "newOrder" }), count: piles.length },
    { id: "review", label: t("Review and save", { ns: "newOrder" }) },
  ], [t, rebars.length, nets.length, piles.length]);

  const commonSectionProps = {
    openItemModal: props.openItemModal,
    onEditItem: props.onEditItem,
    onCopyItem: props.onCopyItem,
    onDeleteItem: props.onDeleteItem,
    onMoveItem: props.onMoveItem,
  };

  return (
    <main className="mobile-order" dir={rightToLeft ? "rtl" : "ltr"}>
      <header className="mobile-order__title">
        <span>{props.edit ? t("Edit order", { ns: "newOrder" }) : t("new order", { ns: "header" })}</span>
        <strong>{props.order?.name || props.order?.order_no || t("Order Details", { ns: "newOrder" })}</strong>
      </header>

      <nav className="mobile-order__steps" aria-label={t("Order Details", { ns: "newOrder" })}>
        {steps.map((step, index) => (
          <button
            key={step.id}
            type="button"
            className={activeStep === step.id ? "is-active" : ""}
            aria-current={activeStep === step.id ? "step" : undefined}
            onClick={() => setActiveStep(step.id)}
          >
            <span>{index + 1}</span>
            {step.label}
            {typeof step.count === "number" && <small>{step.count}</small>}
          </button>
        ))}
      </nav>

      <div className="mobile-order__content">
        {activeStep === "details" && (
          <section className="mobile-order-section mobile-order-details">
            <h1>{t("Order Details", { ns: "newOrder" })}</h1>
            {props.detailsContent}
            <button type="button" className="mobile-order-next" onClick={() => setActiveStep("rebars")}>
              {t("Continue to items", { ns: "newOrder" })}
            </button>
          </section>
        )}
        {activeStep === "rebars" && (
          <ItemSection kind="rebar" items={rebars} title={t("Rebars", { ns: "newOrder" })} addLabel={t("Add New Rebar", { ns: "newOrder" })} {...commonSectionProps} />
        )}
        {activeStep === "nets" && (
          <ItemSection kind="net" items={nets} title={t("Nets", { ns: "newOrder" })} addLabel={t("add new net", { ns: "newOrder" })} {...commonSectionProps} />
        )}
        {activeStep === "piles" && (
          <ItemSection kind="pile" items={piles} title={t("Cages", { ns: "newOrder" })} addLabel={t("add new cage", { ns: "newOrder" })} {...commonSectionProps} />
        )}
        {activeStep === "review" && (
          <section className="mobile-order-section mobile-order-review">
            <h1>{t("Review and save", { ns: "newOrder" })}</h1>
            <div className="mobile-order-review__counts">
              <span>{t("Rebars", { ns: "newOrder" })}<strong>{rebars.length}</strong></span>
              <span>{t("Nets", { ns: "newOrder" })}<strong>{nets.length}</strong></span>
              <span>{t("Cages", { ns: "newOrder" })}<strong>{piles.length}</strong></span>
            </div>
          </section>
        )}
      </div>

      {(activeStep === "review" || (props.isSupplier && !props.edit)) && (
        <section className="mobile-order-section mobile-order-review">
          <ManageOrder {...props} summaryOnly={activeStep !== "review"} />
        </section>
      )}

      {activeStep !== "review" && (
        <button type="button" className="mobile-order-review-shortcut" onClick={() => setActiveStep("review")}>
          {t("Review and save", { ns: "newOrder" })}
          <span>{rebars.length + nets.length + piles.length}</span>
        </button>
      )}
    </main>
  );
}

export default OrderMobile;